import { Throttler } from "@tanstack/pacer/throttler";

import { createLogger } from "@/logger";

const log = createLogger("feed");

import { flags } from "@/config";
import { idx, rowsByIndex, store } from "@/store/store";
import type { Item, WorkspaceMembership } from "@/store/types";
import {
  DRIVER_ITEM_READ_EVENTS,
  FEED_CLEAR_EVENTS,
  ITEM_READ_EVENTS,
  type ReadModel,
  readModelForWorkspace,
  WATERMARK_CLEAR_EVENTS,
} from "./readEvents";

const fetchHideDriverMessageValue = (organizer: boolean) => {
  const hideDriverMessagesString = localStorage?.getItem("hideDriverMessages") ?? false;
  return hideDriverMessagesString === "true" && organizer;
};

type UnreadScope = {
  /** Restrict to alias / non-alias feeds; undefined = both. */
  isAliasFeed?: boolean;
  /** Count clear-events from every organizer in the workspace, not just mine. */
  teamDispatchMode?: boolean;
  /** Only flip items authored by alias members (the organizer alias-feed rule). */
  isFromAliasMember?: boolean;
};

/** The receipts one unread pass reads: those that read a feed up to them, and those that read only their item. */
type ReadReceipts = { feedClears: ReadonlySet<string>; itemReads: ReadonlySet<string> };

const WATERMARK_RECEIPTS: ReadReceipts = { feedClears: new Set(WATERMARK_CLEAR_EVENTS), itemReads: new Set() };
const PER_ITEM_FEED_CLEARS: ReadonlySet<string> = new Set(FEED_CLEAR_EVENTS);

/** What a feed's read events say: the watermark every earlier item sits under, and the items read one by one. */
type FeedReadState = { threshold: string; readItemIds: ReadonlySet<string> };

/**
 * The per-feed read state from the receipts of `clearAccountIds` (mine, or every organizer's in team-dispatch mode).
 *
 * The threshold is the newest feed clear or the permission update, whichever is later; items created after it are
 * unread unless an item read names them.
 */
function feedReadState(
  feedId: string,
  permissionUpdatedAt: string | null,
  clearAccountIds: ReadonlySet<string>,
  receipts: ReadReceipts,
): FeedReadState {
  let latestEvent: string | null = null;
  const readItemIds = new Set<string>();
  for (const event of rowsByIndex("accountEvent", idx.accountEventsByFeed, feedId)) {
    if (!clearAccountIds.has(event.accountId)) continue;
    if (receipts.feedClears.has(event.name)) {
      if (latestEvent === null || event.createdAt > latestEvent) latestEvent = event.createdAt;
    } else if (event.itemId != null && receipts.itemReads.has(event.name)) {
      readItemIds.add(event.itemId);
    }
  }
  const threshold =
    latestEvent !== null && (permissionUpdatedAt === null || latestEvent > permissionUpdatedAt)
      ? latestEvent
      : permissionUpdatedAt;
  // Empty string compares below every ISO timestamp — the old -infinity sentinel.
  return { threshold: threshold ?? "", readItemIds };
}

/**
 * The receipts that count for this membership under `readModel`.
 *
 * The watermark reads a feed up to any clear event, for every role, and never reads one item alone. Per item, only
 * "Marked Feed as Read" reads the feed and every other receipt reads only its item, so reading the newest message says
 * nothing about the older ones; a driver's listening counts there, a dispatcher's does not.
 */
function readReceiptsFor(membership: WorkspaceMembership, readModel: ReadModel): ReadReceipts {
  if (readModel === "watermark") return WATERMARK_RECEIPTS;
  const itemReads =
    membership.role === "limitedMember" ? [...ITEM_READ_EVENTS, ...DRIVER_ITEM_READ_EVENTS] : ITEM_READ_EVENTS;
  return { feedClears: PER_ITEM_FEED_CLEARS, itemReads: new Set(itemReads) };
}

function itemQualifies(item: Item, membership: WorkspaceMembership, scope: UnreadScope): boolean {
  // SQL `!=` semantics: a null accountId never matches the inequality.
  if (item.accountId == null || item.accountId === membership.accountId) return false;
  if (scope.isFromAliasMember !== undefined && item.isFromAliasMember !== scope.isFromAliasMember) return false;
  return item.unreadNonMutable === false && item.isFromBot === false;
}

function applyUnreadPass(
  membership: WorkspaceMembership,
  feedIds: string[] | undefined,
  alsoSetAsRead: boolean,
  readModel: ReadModel,
  scope: UnreadScope,
): number {
  const clearAccountIds = new Set<string>();
  if (scope.teamDispatchMode) {
    for (const member of rowsByIndex("workspaceMembership", idx.membershipsByWorkspace, membership.workspaceId)) {
      if (member.role === "member" && member.accountId != null) clearAccountIds.add(member.accountId);
    }
  } else if (membership.accountId != null) {
    clearAccountIds.add(membership.accountId);
  }

  const feedFilter = feedIds !== undefined ? new Set(feedIds) : null;
  const unreadById = new Map<string, boolean>();
  const receipts = readReceiptsFor(membership, readModel);

  for (const permission of rowsByIndex("permission", idx.permissionsByMembership, membership.id)) {
    if (permission.name !== "read" || !permission.enabled || permission.feedId == null) continue;
    if (feedFilter !== null && !feedFilter.has(permission.feedId)) continue;
    // The old SQL only joined `feed` when an isAliasFeed scope applied; a permission whose feed
    // row hasn't synced yet still gets its unread pass.
    if (scope.isAliasFeed !== undefined) {
      const feed = store.get("feed", permission.feedId);
      if (!feed || feed.isAliasFeed !== scope.isAliasFeed) continue;
    }

    const { threshold, readItemIds } = feedReadState(
      permission.feedId,
      permission.updatedAt,
      clearAccountIds,
      receipts,
    );
    for (const item of rowsByIndex("item", idx.itemsByFeed, permission.feedId)) {
      if (!itemQualifies(item, membership, scope)) continue;
      if (item.createdAt > threshold && !readItemIds.has(item.id)) {
        if (item.unread !== true) unreadById.set(item.id, true);
      } else if (alsoSetAsRead && item.unread === true) {
        unreadById.set(item.id, false);
      }
    }
  }
  store.updateMany("item", unreadById.keys(), (row) => {
    const unread = unreadById.get(row.id);
    return unread === undefined || row.unread === unread ? row : { ...row, unread };
  });
  return unreadById.size;
}

export const UNREADS_ALSO_SET_AS_READ_ON_NEXT_BOOTSTRAP_KEY = "storyboard.unreadsAlsoSetAsReadOnNextBootstrap.v1";

/** Use before a full page reload (e.g. `hardReboot`); the next bootstrap should run `initializeUnreadsForAllFeeds` with `alsoSetAsRead: true`. In-memory debounced unread work is cleared by reload. */
export function setUnreadAlsoSetAsReadOnNextBootstrap(): void {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(UNREADS_ALSO_SET_AS_READ_ON_NEXT_BOOTSTRAP_KEY, "1");
    }
  } catch {
    // quota / private mode
  }
}

export function consumeUnreadAlsoSetAsReadOnNextBootstrap(): boolean {
  try {
    if (typeof sessionStorage === "undefined") {
      return false;
    }
    if (sessionStorage.getItem(UNREADS_ALSO_SET_AS_READ_ON_NEXT_BOOTSTRAP_KEY) === "1") {
      sessionStorage.removeItem(UNREADS_ALSO_SET_AS_READ_ON_NEXT_BOOTSTRAP_KEY);
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

export async function initializeUnreadsForAllFeeds({
  membership,
  feedIds,
  alsoSetAsRead = false,
}: {
  membership: WorkspaceMembership;
  feedIds?: string[];
  alsoSetAsRead: boolean;
}) {
  const startTime = new Date().getTime();
  const isOrganizer = membership.role === "member";
  const isDriver = membership.role === "limitedMember";
  const teamDispatch = fetchHideDriverMessageValue(isOrganizer);
  const readModel = readModelForWorkspace(membership.workspaceId);

  let affectedRows = 0;
  try {
    store.batch(() => {
      if (isOrganizer && teamDispatch) {
        affectedRows += applyUnreadPass(membership, feedIds, alsoSetAsRead, readModel, { isAliasFeed: false });
        affectedRows += applyUnreadPass(membership, feedIds, alsoSetAsRead, readModel, {
          isAliasFeed: true,
          teamDispatchMode: true,
          isFromAliasMember: true,
        });
      } else if (isDriver) {
        affectedRows += applyUnreadPass(membership, feedIds, alsoSetAsRead, readModel, {});
      } else {
        affectedRows += applyUnreadPass(membership, feedIds, alsoSetAsRead, readModel, {
          isAliasFeed: true,
          isFromAliasMember: true,
        });
        affectedRows += applyUnreadPass(membership, feedIds, alsoSetAsRead, readModel, { isAliasFeed: false });
      }
    });
  } catch (e) {
    log.error("ERROR", e);
  }
  const endTime = new Date().getTime();
  log.debug(feedIds !== undefined ? "[UNREADS][FEEDS]" : "[UNREADS][INITIAL]", endTime - startTime, {
    membership,
    feedIds,
    alsoSetAsRead,
    isOrganizer,
    isDriver,
    teamDispatch,
    readModel,
    affectedRows,
  });
}

// Queue for debouncing unread calculations
type UnreadRequest = {
  membership: WorkspaceMembership;
  feedIds?: string[];
  alsoSetAsRead: boolean;
};

// Use an array so every enqueue call is preserved regardless of timing.
// A keyed object with Date.now() would silently drop requests that arrive
// within the same millisecond tick (e.g. a "Mark as Read" action and a
// concurrent AppSync event), causing the affected feed to stay unread.
const unreadQueue: UnreadRequest[] = [];
const DEFAULT_UNREAD_DEBOUNCE_MS = 2000;

// Process the queued unread calculation requests
function processUnreadQueue() {
  const requests = unreadQueue.splice(0);

  const memberships = new Map<string, UnreadRequest>();

  // Group by membership and alsoSetAsRead, combining feedIds.
  // When either operand is undefined ("all feeds"), the merged scope must
  // remain undefined — a specific list must never narrow an "all feeds" scope.
  for (const request of requests) {
    const key = `${request.membership.id}:${request.alsoSetAsRead}`;

    if (memberships.has(key)) {
      const existing = memberships.get(key)!;
      if (existing.feedIds != null && request.feedIds != null) {
        // Both specific — merge without duplicates
        existing.feedIds = [...new Set([...existing.feedIds, ...request.feedIds])];
      } else {
        // At least one is "all feeds" — "all feeds" wins
        existing.feedIds = undefined;
      }
    } else {
      memberships.set(key, {
        membership: request.membership,
        feedIds: request.feedIds != null ? [...request.feedIds] : undefined,
        alsoSetAsRead: request.alsoSetAsRead,
      });
    }
  }

  // Process each combined request (only if there are any)
  if (memberships.size > 0) {
    memberships.forEach((request) => {
      initializeUnreadsForAllFeeds(request).catch((error) => {
        log.error("Failed to process unread calculation", error);
      });
    });
  }
}

function unreadDebounceMs(): number {
  return typeof flags.unreadDebounceMs === "number" ? flags.unreadDebounceMs : DEFAULT_UNREAD_DEBOUNCE_MS;
}

/**
 * Leading + trailing over a fixed window rather than a debounce, which is what keeps a steady
 * event stream moving: the common single event (opening a channel) recomputes immediately instead
 * of waiting out the window, bursts (AppSync floods, mark-all-read) coalesce into one recompute per
 * window, and a stream that never goes quiet still recomputes once per window instead of starving
 * behind a timer that every arrival restarts. `wait` is a function, so the LaunchDarkly flag tunes
 * the window live.
 */
const unreadThrottler = new Throttler(processUnreadQueue, {
  leading: true,
  trailing: true,
  wait: unreadDebounceMs,
});

export function enqueueCalculateUnreads({
  membership,
  feedIds,
  alsoSetAsRead = false,
}: {
  membership: WorkspaceMembership;
  feedIds?: string[];
  alsoSetAsRead: boolean;
}) {
  unreadQueue.push({ membership, feedIds, alsoSetAsRead });
  unreadThrottler.maybeExecute();
}

/**
 * Runs a pass the throttle is holding for its trailing edge now, instead of at the end of its window.
 *
 * For a caller that just stored rows a member can see: until the pass runs, they read as already read.
 */
export function flushCalculateUnreads() {
  unreadThrottler.flush();
}

/*

As an organizer
Alias feed:
  Only the alias owner should trigger unread
  Only use shared account events if hideDriverMessagesWhenAnotherOrganizerReadsIt is true

Non-alias feed:
  Bots never trigger unread

*/
